import {
  useExportSavingsAccountTransHistoriesMutation,
  useExportSavingsRolloverHistoriesMutation,
  useLazyGetSavingsAccountTransHistoriesQuery,
  useLazyGetSavingsRolloverHistoriesQuery,
} from "@/services/savingsAPI";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Flex,
  Group,
  ScrollArea,
  Space,
  Table,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { IconDownload, IconFilter, IconSearch } from "@tabler/icons-react";
import { get } from "lodash";
import FilterDrawer from "./FilterDrawer";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import ExportAccountTransactionHistory from "@/modules/accounts/components/detail-current-account-panel/components/ExportAccountTransactionHistory";
import PaginationInfo from "@/components/tables/pagination/PaginationInfo";
import PaginationControls from "@/components/tables/pagination/PaginationControls";

const PAGE_SIZE = 10;

interface Props {
  accountNo: string;
  accountName: string;
}
const HistorySaving = ({ accountNo, accountName }: Props) => {
  const { t } = useTranslation();
  const [titlePopup, setTitlePopup] = useState<string | undefined>(undefined);
  const [exportFileUrl, setExportFileUrl] = useState<string>("");
  const [
    isOpenContractInformation,
    { open: openContractInformation, close: closeContractInformation },
  ] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      fromDate: "",
      toDate: "",
      isIncoming: "",
      fromAmount: "",
      toAmount: "",
    },
  });
  const [triggerHistory, { data: dataHistory }] =
    useLazyGetSavingsAccountTransHistoriesQuery();

  const [triggerRollover, { data: dataRollover }] =
    useLazyGetSavingsRolloverHistoriesQuery();
  const histories = get(dataHistory, "body.fundsTransfer.items", []);
  const historyTotalRecords = get(dataHistory, "body.totalRecords", 0);

  console.log("historyTotalRecords", historyTotalRecords);
  const historyTotalPages = Math.max(
    1,
    get(dataHistory, "body.fundsTransfer.totalPages", 0) ||
      Math.ceil(historyTotalRecords / PAGE_SIZE),
  );
  const rollovers = get(dataRollover, "body.histories", []);
  const rolloverTotalRecords = get(dataRollover, "body.totalRecords", 0);
  const rolloverTotalPages = Math.max(
    1,
    get(dataRollover, "body.totalPages", 0) ||
      Math.ceil(rolloverTotalRecords / PAGE_SIZE),
  );
  const [openedFilter, { open: openFilter, close: closeFilter }] =
    useDisclosure(false);
  const [type, setType] = useState<"history" | "rollover">("history");
  const [historyPage, setHistoryPage] = useState(1);
  const [rolloverPage, setRolloverPage] = useState(1);
  const [exportSavingsAccountTransHistories] =
    useExportSavingsAccountTransHistoriesMutation();

  const [exportSavingsRolloverHistories] =
    useExportSavingsRolloverHistoriesMutation();

  const downloadFile = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "file.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const historyPayload = useMemo(() => {
    if (type !== "history") return null;
    return {
      accountNo,
      fromDate: form.values.fromDate,
      toDate: form.values.toDate,
      isIncoming: form.values.isIncoming === "INCOMING",
      page: historyPage,
      fromAmount: form.values.fromAmount,
      toAmount: form.values.toAmount,
      pageSize: PAGE_SIZE,
    };
  }, [
    type,
    historyPage,
    accountNo,
    form.values.fromDate,
    form.values.toDate,
    form.values.isIncoming,
    form.values.fromAmount,
    form.values.toAmount,
  ]);

  const rolloverPayload = useMemo(() => {
    if (type === "history") return null;
    return {
      accountNo,
      fromDate: form.values.fromDate,
      toDate: form.values.toDate,
      minAmount: form.values.fromAmount,
      maxAmount: form.values.toAmount,
      page: rolloverPage,
      pageSize: PAGE_SIZE,
    };
  }, [
    type,
    rolloverPage,
    accountNo,
    form.values.fromDate,
    form.values.toDate,
    form.values.fromAmount,
    form.values.toAmount,
  ]);

  const handleCallApi = () => {
    if (type === "history") {
      triggerHistory(historyPayload!);
    } else {
      triggerRollover(rolloverPayload!);
    }
  };

  const getFileUrl = (result: any) =>
    result?.body?.fileUrl ||
    result?.body?.data?.fileUrl ||
    result?.body?.fileName ||
    "";

  const showFileUrlError = () => {
    showNotification({
      title: `${t("ERROR")}`,
      message: `${t("NO_FILE_URL_FROM_SYSTEM")}`,
      color: "red",
    });
  };

  const handleExport = async () => {
    openContractInformation();
    try {
      const isHistory = type === "history";
      setTitlePopup(
        isHistory
          ? `${t("EXPORT_TRANSACTION_HISTORY")}`
          : `${t("EXPORT_ROLLOVER_HISTORY")}`,
      );
      const result = isHistory
        ? await exportSavingsAccountTransHistories(historyPayload!).unwrap()
        : await exportSavingsRolloverHistories(rolloverPayload!).unwrap();
      const fileUrl = getFileUrl(result);
      if (!fileUrl) {
        showFileUrlError();
        return;
      }
      setExportFileUrl(fileUrl);
    } catch (error) {
      console.error("Error exporting file:", error);
    }
  };

  const handleForm = (values: any) => {
    // Xử lý giá trị từ form lọc ở đây
    if (type === "history") {
      setHistoryPage(1);
    } else {
      setRolloverPage(1);
    }
    handleCallApi();
    console.log("Filter values:", values);
    closeFilter();
  };

  useEffect(() => {
    handleCallApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, historyPage, rolloverPage]);

  return (
    <Card padding="lg" radius="md">
      <Tabs
        value={type}
        onTabChange={(val) => {
          if (val) setType(val as "history" | "rollover");
        }}
        styles={{ tabsList: { borderBottom: "none" } }}
      >
        <Flex justify="space-between" align="center">
          <Tabs.List>
            <Tabs.Tab value="history">{t("TRANSACTION_HISTORY")}</Tabs.Tab>
            <Tabs.Tab value="rollover">{t("RENEWAL_HISTORY")}</Tabs.Tab>
          </Tabs.List>
          <Button
            variant="outline"
            leftIcon={<IconDownload />}
            onClick={handleExport}
            style={{ borderRadius: "8px" }}
          >
            {t("EXPORT_FILE")}
          </Button>
        </Flex>

        {/* Nội dung Tab “Thẻ” */}
        <Tabs.Panel value="history" pt="md">
          <Flex gap={8} align="center">
            <TextInput
              placeholder={`${t("SEARCH")}`}
              radius={8}
              rightSection={<IconSearch size={16} />}
              style={{ width: "250px" }}
            />
            <ActionIcon
              variant="outline"
              radius="md"
              size={34}
              color="blue"
              onClick={() => {
                setType("history");
                openFilter();
              }}
            >
              <IconFilter size={18} />
            </ActionIcon>
          </Flex>
          <Space h={16} />
          <ScrollArea h={280}>
            <Table verticalSpacing="sm" highlightOnHover>
              <thead>
                <tr>
                  <th>{t("NOSTT")}</th>
                  <th>{t("TRANSACTION_CODE")}</th>
                  <th>{t("TRANSACTION_DATE")}</th>
                  <th>{t("TRANSACTION_AMOUNT")}</th>
                  <th>{t("DESCRIPTION")}</th>
                </tr>
              </thead>
              <tbody>
                {histories.map((item, index) => (
                  <tr key={item.transId}>
                    <td>{(historyPage - 1) * PAGE_SIZE + index + 1}</td>
                    <td>{item.transId}</td>
                    <td>
                      <Box>
                        <Text>{item.displayDate}</Text>
                        {/* <Text>{item.displayTime}</Text> */}
                      </Box>
                    </td>
                    <td
                      style={
                        item.transType === "TRANSFER_OUT"
                          ? { color: "#F65858" }
                          : { color: "#34B529" }
                      }
                    >
                      {item.displayAmount}
                    </td>
                    <td>{item.fromMessage || t(item.transType, { defaultValue: item.transType })}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
          {historyTotalRecords > 0 && (
            <Group position="apart" sx={{ padding: "12px 4px" }}>
              <PaginationInfo
                current={(historyPage - 1) * PAGE_SIZE + 1}
                total={historyTotalRecords}
              />
              <PaginationControls
                currentPage={historyPage}
                totalPages={historyTotalPages}
                onPageChange={setHistoryPage}
                withEdges
              />
            </Group>
          )}
        </Tabs.Panel>

        {/* Các tab khác (chưa có dữ liệu) */}
        <Tabs.Panel value="rollover" pt="md">
          <Flex gap={8} align="center">
            <TextInput
              placeholder={`${t("SEARCH")}`}
              radius={8}
              rightSection={<IconSearch size={16} />}
              style={{ width: "250px" }}
            />
            <ActionIcon
              variant="outline"
              radius="md"
              size={34}
              color="blue"
              onClick={() => {
                setType("rollover");
                openFilter();
              }}
            >
              <IconFilter size={18} />
            </ActionIcon>
          </Flex>
          <Space h={16} />
          <ScrollArea h={250}>
            <Table verticalSpacing="sm" highlightOnHover>
              <thead>
                <tr>
                  <th>{t("NOSTT")}</th>
                  <th>{t("PRINCIPAL_AMOUNT")}</th>
                  <th>{t("INTEREST_RATE_(%/YEAR)")}</th>
                  <th>{t("INTEREST_AMOUNT")}</th>
                  <th>{t("START_DATE")}</th>
                  <th>{t("MATURITY_DATE")}</th>
                </tr>
              </thead>
              <tbody>
                {rollovers.map((item, index) => (
                  <tr key={item.transId}>
                    <td>{(rolloverPage - 1) * PAGE_SIZE + index + 1}</td>
                    <td style={{ color: "#0171BB" }}>
                      {item.principalAmountDisplay}
                    </td>
                    <td>{item.interestRateDisplay}</td>
                    <td>{item.interestAmountDisplay}</td>
                    <td>{item.fromDateDisplay}</td>
                    <td>{item.toDateDisplay}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
          {rolloverTotalRecords > 0 && (
            <Group position="apart" sx={{ padding: "12px 4px" }}>
              <PaginationInfo
                current={(rolloverPage - 1) * PAGE_SIZE + 1}
                total={rolloverTotalRecords}
              />
              <PaginationControls
                currentPage={rolloverPage}
                totalPages={rolloverTotalPages}
                onPageChange={setRolloverPage}
                withEdges
              />
            </Group>
          )}
        </Tabs.Panel>
      </Tabs>
      <FilterDrawer
        opened={openedFilter}
        onClose={closeFilter}
        onSubmit={handleForm}
        form={form}
        type={type}
        accountName={accountName}
        accountNo={accountNo}
      />
      <ExportAccountTransactionHistory
        opened={isOpenContractInformation}
        onClose={closeContractInformation}
        fileUrl={exportFileUrl}
        loading={false}
        title={titlePopup}
        onDowload={() => {
          if (exportFileUrl) downloadFile(exportFileUrl);
        }}
      />
    </Card>
  );
};
export default HistorySaving;
